import { useMemo, useState } from "react";
import axios from "axios";
import { Plus, Trash2, Camera, CheckCircle2, Loader2, Tag } from "lucide-react";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;
const emptyRow = () => ({ width: "", height: "", quantity: 1 });

// Downscale phone photos (often 5-10MB) to a ~1600px JPEG before upload.
const compressImage = (file) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      c.toBlob((b) => resolve(b ? new File([b], "measurement.jpg", { type: "image/jpeg" }) : file), "image/jpeg", 0.85);
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });

function SizeRow({ row, index, onChange, onRemove, canRemove }) {
  const input = "h-12 w-full rounded-lg border-2 border-zinc-200 px-3 text-base focus:border-[#207024] outline-none";
  return (
    <div className="flex items-end gap-2" data-testid={`measure-row-${index}`}>
      <label className="flex-1 min-w-0">
        <span className="text-xs text-zinc-500">Width (in)</span>
        <input className={input} type="number" inputMode="decimal" step="0.0625" min="1" placeholder="34.5"
          value={row.width} onChange={(e) => onChange({ width: e.target.value })} data-testid={`measure-width-${index}`} />
      </label>
      <span className="pb-3 text-zinc-400">×</span>
      <label className="flex-1 min-w-0">
        <span className="text-xs text-zinc-500">Height (in)</span>
        <input className={input} type="number" inputMode="decimal" step="0.0625" min="1" placeholder="48"
          value={row.height} onChange={(e) => onChange({ height: e.target.value })} data-testid={`measure-height-${index}`} />
      </label>
      <label className="w-16 shrink-0">
        <span className="text-xs text-zinc-500">Qty</span>
        <input className={input} type="number" inputMode="numeric" min="1" max="50"
          value={row.quantity} onChange={(e) => onChange({ quantity: e.target.value })} data-testid={`measure-qty-${index}`} />
      </label>
      <button type="button" onClick={onRemove} disabled={!canRemove}
        className="h-12 w-10 shrink-0 flex items-center justify-center text-zinc-400 hover:text-red-600 disabled:opacity-20"
        data-testid={`measure-remove-${index}`}>
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

function UpsellBanner({ percent, minScreens, total }) {
  const unlocked = total >= minScreens;
  return (
    <div className={`rounded-xl p-3 border-2 flex gap-3 items-start ${unlocked ? "bg-green-50 border-green-300" : "bg-amber-50 border-amber-300"}`}
      data-testid="upsell-banner">
      <Tag className={`w-5 h-5 shrink-0 mt-0.5 ${unlocked ? "text-green-700" : "text-amber-600"}`} />
      <div className="text-sm">
        <div className="font-bold text-zinc-900">Add more screens to this order and save {percent}%</div>
        <div className="text-zinc-600" data-testid="upsell-status">
          {unlocked
            ? `Unlocked: ${percent}% off your whole order (${total} screens).`
            : `Measure ${minScreens - total} more screen${minScreens - total === 1 ? "" : "s"} to get ${percent}% off everything.`}
        </div>
      </div>
    </div>
  );
}

export const MeasurementSubmitForm = ({ article }) => {
  const cfg = article.measurement_form || {};
  const customerRef = useMemo(() => new URLSearchParams(window.location.search).get("c") || "", []);
  const [rows, setRows] = useState([emptyRow()]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [photo, setPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const total = rows.reduce((s, r) => s + (r.width && r.height ? Number(r.quantity) || 0 : 0), 0);
  const showUpsell = cfg.upsell_enabled && Number(cfg.upsell_percent) > 0;
  const minScreens = Number(cfg.upsell_min_screens) || 2;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const filled = rows.filter((r) => r.width || r.height);
    if (!filled.length || filled.some((r) => !r.width || !r.height)) {
      setError("Enter a width and height for each screen.");
      return;
    }
    if (!customerRef && phone.replace(/\D/g, "").length < 10) {
      setError("Enter your mobile number so we know who you are.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify({ customer_ref: customerRef || null, name, phone, notes, rows: filled }));
      if (photo) fd.append("photo", await compressImage(photo));
      const { data } = await axios.post(`${API}/public/guides/${article.article_id}/measurements`, fd);
      setDone(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please text us your sizes instead.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section className="mt-10 rounded-2xl bg-green-50 border-2 border-green-200 p-6 text-center" data-testid="measure-success">
        <CheckCircle2 className="w-10 h-10 text-[#207024] mx-auto mb-2" />
        <h3 className="text-xl font-bold text-zinc-900">Got it, thanks!</h3>
        <p className="text-zinc-600 mt-1">
          We received {done.total_quantity} screen size{done.total_quantity === 1 ? "" : "s"} and will text you shortly.
          {done.upsell_claimed && ` Your ${done.upsell_percent}% off is locked in.`}
        </p>
      </section>
    );
  }

  return (
    <section className="mt-10 rounded-2xl border-2 border-[#207024]/30 bg-white p-4 sm:p-6 space-y-4" data-testid="measure-form">
      <div>
        <h3 className="text-xl font-bold text-zinc-900">Submit my measurements</h3>
        <p className="text-sm text-zinc-600">Width first, then height, in inches. Decimals are fine (34.5 = 34 1/2).</p>
      </div>
      {showUpsell && <UpsellBanner percent={cfg.upsell_percent} minScreens={minScreens} total={total} />}
      <div className="space-y-3">
        {rows.map((r, i) => (
          <SizeRow key={i} row={r} index={i} canRemove={rows.length > 1}
            onChange={(patch) => setRows(rows.map((x, j) => (j === i ? { ...x, ...patch } : x)))}
            onRemove={() => setRows(rows.filter((_, j) => j !== i))} />
        ))}
      </div>
      <button type="button" onClick={() => setRows([...rows, emptyRow()])} disabled={rows.length >= 30}
        className="h-11 w-full rounded-lg border-2 border-dashed border-zinc-300 text-zinc-700 font-semibold flex items-center justify-center gap-1 hover:border-[#207024] hover:text-[#207024]"
        data-testid="measure-add-row">
        <Plus className="w-4 h-4" /> Add another screen size
      </button>
      {!customerRef && (
        <div className="grid sm:grid-cols-2 gap-3">
          <input className="h-12 rounded-lg border-2 border-zinc-200 px-3 focus:border-[#207024] outline-none" placeholder="Your name"
            value={name} onChange={(e) => setName(e.target.value)} data-testid="measure-name" />
          <input className="h-12 rounded-lg border-2 border-zinc-200 px-3 focus:border-[#207024] outline-none" placeholder="Mobile number"
            type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="measure-phone" />
        </div>
      )}
      <textarea className="w-full rounded-lg border-2 border-zinc-200 px-3 py-2 focus:border-[#207024] outline-none" rows={2}
        placeholder="Anything else? (pet screen, color, which window…)" value={notes} onChange={(e) => setNotes(e.target.value)}
        data-testid="measure-notes" />
      <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
        <span className="h-10 px-3 rounded-lg bg-zinc-100 flex items-center gap-1.5 font-semibold"><Camera className="w-4 h-4" /> {photo ? "Change photo" : "Add a photo (optional)"}</span>
        <span className="truncate text-zinc-500" data-testid="measure-photo-name">{photo?.name}</span>
        <input type="file" accept="image/*" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] || null)} data-testid="measure-photo" />
      </label>
      {error && <div className="text-sm text-red-600" data-testid="measure-error">{error}</div>}
      <button type="button" onClick={submit} disabled={submitting}
        className="h-12 w-full rounded-full bg-[#207024] hover:bg-[#1a5c1d] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
        data-testid="measure-submit">
        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
        Send my measurements{total > 0 ? ` (${total})` : ""}
      </button>
    </section>
  );
};
